import { Link } from 'react-router-dom';

/**
 * Privacy policy (card t_31859fc7 §7, safeguarding plan §3.3.5).
 *
 * Plain English, on purpose — this is the page a coach reads before uploading footage of other
 * people's children, and the page a share recipient lands on from the footer. The same text is
 * served from the worker at `/privacy` so it is reachable without the app bundle.
 */
const SECTIONS = [
  {
    id: 'what-we-store',
    heading: 'What we store',
    body: (
      <ul className="mt-2 list-disc space-y-1 pl-5">
        <li>The video you upload, and a 720p copy for playback.</li>
        <li>The audio you record while you talk over the video.</li>
        <li>The transcript of that audio, and the clips the AI proposes from it.</li>
        <li>The clips you confirm and render, and any highlight reels you build.</li>
        <li>
          Your account email, your club&apos;s branding (colours, name, logo), and a counter of how
          many times each share link has been opened.
        </li>
      </ul>
    ),
  },
  {
    id: 'how-long',
    heading: 'How long we keep it',
    body: (
      <ul className="mt-2 list-disc space-y-1 pl-5">
        <li>
          Free tier: <strong>3 months</strong>. After that the footage, the audio and the clips are
          deleted automatically.
        </li>
        <li>
          Pro tier (coming): <strong>12 months</strong>.
        </li>
      </ul>
    ),
  },
  {
    id: 'who-can-see',
    heading: 'Who can see it',
    body: (
      <p className="mt-2">
        Only you, and anyone you send a share link to. There is no public gallery, no browse-by-team
        and no way to find a clip without its link. Share links can be set to expire and can be
        revoked at any time — a revoked or expired link stops working immediately. We do not sell
        your data and we do not use your footage to train models.
      </p>
    ),
  },
  {
    id: 'how-to-delete',
    heading: 'How to delete it',
    body: (
      <p className="mt-2">
        Open your <Link to="/account" className="text-copper-700 underline">account page</Link> and
        choose <strong>Delete account</strong>. That deletes your footage, audio, clips, reels and
        share links from storage and from our database — the cascade completes within 48 hours, and
        we email you to confirm it. Deleting your account cannot be undone.
      </p>
    ),
  },
  {
    id: 'children',
    heading: "Children's footage",
    body: (
      <p className="mt-2">
        If footage shows players under 18, Callout asks you to confirm you have consent before it
        will store anything. Without that confirmation nothing is uploaded.
      </p>
    ),
  },
] as const;

export function PrivacyRoute() {
  return (
    <article className="max-w-2xl space-y-6" data-testid="privacy-page">
      <header>
        <h1 className="text-xl font-semibold tracking-tight">Privacy</h1>
        <p className="mt-1 text-sm text-charcoal-500">
          Callout stores match footage that often includes children. This page says, in plain
          English, what we keep, for how long, who can see it, and how to get rid of it.
        </p>
      </header>

      {SECTIONS.map((section) => (
        <section
          key={section.id}
          data-testid={`privacy-section-${section.id}`}
          className="rounded-lg border border-charcoal-500/20 bg-white/60 px-4 py-3"
        >
          <h2 className="text-sm font-semibold uppercase tracking-wide text-charcoal-500">
            {section.heading}
          </h2>
          <div className="text-sm text-charcoal-800">{section.body}</div>
        </section>
      ))}

      <p className="text-xs text-charcoal-500">
        Questions: support@callout.app. Legal review happens before public launch.
      </p>
    </article>
  );
}
